/* eslint-disable @typescript-eslint/no-unsafe-enum-comparison */
/* eslint-disable @typescript-eslint/no-unnecessary-type-conversion */

import { Headline, LayoutItem, Tag, TagGroup, Text } from '@oneaudi/unified-web-components';
import { Tag as AlphaTag } from '@oneaudi/unified-web-alpha-components';
import { FootnoteReferenceV2 } from '@oneaudi/feature-app-utils';
import React from 'react';
import { getOptimizedImageSrc, getSizes, getSrcSet } from '../../utils/imgUtils';
import type { MoxxCarline, MoxxCarlineGroup } from '../../../@types/MoxxTypes';
import { useAppContext } from '../../context/Context';
import { useInitialState } from '../../context/InitialStateContext';
import { matchI18n, useI18n } from '../../i18n';

import CardButtons from './CardButtons';
import ConsumptionAndEmission from './ConsumptionAndEmission';
import {
  StyledCard,
  StyledCardHeadingContainer,
  StyledCardImg,
  StyledCardImgWrapper,
  StyledGrid,
  StyledInformationSection,
  StyledNewTag,
  TagWrapper,
} from './CardStyles';
import {
  CARGROUP_IMAGE_SIZE_DESKTOP,
  CARGROUP_IMAGE_SIZE_MOBILE,
  CARLINE_IMG_SIZE_DESKTOP,
  CARLINE_IMG_SIZE_MOBILE,
  IMAGE_MIME_TYPE,
} from '../../constants';
import { FuelType, VehicleType } from '../../../web/generated/onegraph';
import { useTheme } from 'styled-components';

export interface CardProps {
  readonly carline: MoxxCarline;
  readonly carlineGroup: MoxxCarlineGroup;
  readonly lazyImage: boolean;
}

const Card: React.FC<CardProps> = ({ carline, carlineGroup, lazyImage }) => {
  const { i18nMessages } = useInitialState();
  const {
    priceFootnotes,
    showBasePrice,
    showVat,
    showCaeData,
    isDiscoverPrimaryBtn,
    showConfigureCTA,
    numberFormatterService,
    showNewCarsCTA,
    showUsedCarsCTA,
  } = useAppContext();
  const electricTag = useI18n('electricTag');
  const hybridTag = useI18n('hybridTag');
  const petrolDieselTag = useI18n('petrolDiesel');
  const petrolTag = useI18n('petrol');
  const dieselTag = useI18n('diesel');
  const newFlagTag = useI18n('markAsNew');

  const cardImageSrc = getOptimizedImageSrc(carline.image, {
    extension: IMAGE_MIME_TYPE,
    width: CARLINE_IMG_SIZE_DESKTOP,
  });

  const newLocal = CARLINE_IMG_SIZE_MOBILE * 2;
  const hasPetrol = carline.fuelTypes.includes(FuelType.Petrol);
  const hasDiesel = carline.fuelTypes.includes(FuelType.Diesel);

  const theme = useTheme();
  const fixed50 = theme.spacing.fixed50;
  const fixed200 = theme.spacing.fixed200;

  return (
    <StyledCard data-testid="card" data-carlineid={carline.id} data-configurator={!carline.isFake}>
      <TagWrapper>
        {(!!carline.markAsNew ||
          carline.vehicleType === 'PHEV' ||
          carline.vehicleType === 'BEV' ||
          carline.vehicleType === 'ICEV') && (
          <TagGroup size="sm">
            {!!carline.markAsNew &&
              (process.env.IS_LEGACY ? (
                <StyledNewTag data-testid="newTag" label={newFlagTag} variant="read-only" />
              ) : (
                <AlphaTag data-testid="newTag" label={newFlagTag} variant="encourage" />
              ))}
            {carline.vehicleType === 'PHEV' && (
              <Tag data-testid="hybridTag" label={hybridTag} variant="read-only" />
            )}
            {carline.vehicleType === 'BEV' && (
              <Tag
                data-testid="electroTag"
                label={electricTag}
                variant="read-only"
                icon="charging"
              />
            )}
            {carline.vehicleType === VehicleType.Icev.toString() && (hasDiesel || hasPetrol) && (
              <Tag
                data-testid="petrolDieselTag"
                label={
                  hasPetrol && hasDiesel
                    ? petrolDieselTag
                    : hasPetrol
                      ? petrolTag
                      : hasDiesel
                        ? dieselTag
                        : undefined
                }
                variant="read-only"
              />
            )}
          </TagGroup>
        )}
      </TagWrapper>
      {cardImageSrc && (
        <StyledCardImgWrapper href={carline.discoverLinkUrl}>
          <StyledCardImg
            src={cardImageSrc}
            srcSet={getSrcSet(
              [
                CARLINE_IMG_SIZE_DESKTOP,
                CARLINE_IMG_SIZE_DESKTOP * 2,
                CARLINE_IMG_SIZE_MOBILE,
                newLocal,
              ].map((width) => {
                return {
                  src: getOptimizedImageSrc(cardImageSrc, { width }),
                  width: `${width}w`,
                };
              }),
            )}
            sizes={getSizes([
              { min: 'md', width: `${CARGROUP_IMAGE_SIZE_DESKTOP}px` },
              { width: `${CARGROUP_IMAGE_SIZE_MOBILE}px` },
            ])}
            loading={lazyImage ? 'lazy' : undefined}
            alt={carline.name}
          />
        </StyledCardImgWrapper>
      )}
      <StyledInformationSection>
        <StyledCardHeadingContainer>
          <Headline
            tag={'h1'}
            variant={{
              type: 'headline',
              order: '4',
              weight: 'normal',
            }}
            data-testid="carlineName"
            margin={[0, 0, showBasePrice ? fixed50 : fixed200, 0]}
          >
            {carline.name}
          </Headline>
          {showBasePrice && (
            <StyledGrid>
              <Text
                variant={{
                  type: 'copy',
                  order: process.env.IS_LEGACY ? '2' : '1',
                  style: 'normal',
                }}
                data-testid="price"
              >
                {carline.minPrice?.value &&
                  !carline.isFake &&
                  matchI18n('basePriceText', i18nMessages, {
                    value: numberFormatterService.formatCurrency(carline.minPrice.value),
                  })}
                {carline.minPrice?.value &&
                  !carline.isFake &&
                  priceFootnotes?.map((footnote: string, i: number) => (
                    <React.Fragment key={footnote}>
                      {i > 0 && <sup>{', '}</sup>}
                      <FootnoteReferenceV2
                        footnoteId={footnote}
                        data-testid={`price-footnote_${i}`}
                      />
                    </React.Fragment>
                  ))}
              </Text>
              {carline.minPrice?.value && !carline.isFake && showVat && (
                <Text variant={{ type: 'copy', order: '2', style: 'normal' }}>
                  {matchI18n('vat', i18nMessages)}
                </Text>
              )}
            </StyledGrid>
          )}
        </StyledCardHeadingContainer>

        <CardButtons
          carline={carline}
          carlineGroup={carlineGroup}
          isPriceDisplayed={
            showBasePrice && !!(carline.minPrice?.value && carline.minPrice.value > 0)
          }
          ctaDisplayOptions={{
            showConfigureCTA,
            showNewCarsCTA,
            showUsedCarsCTA,
            isDiscoverPrimaryBtn,
          }}
        />
        {showCaeData && carline.consumptionAndEmission && (
          <LayoutItem data-testid="consumptionAndEmission">
            <ConsumptionAndEmission
              consumptionData={carline.consumptionAndEmission}
              carlineName={carline.name}
            />
          </LayoutItem>
        )}
      </StyledInformationSection>
    </StyledCard>
  );
};

export default Card;
